import {
  EmptyData, Flex, FlexColumn, LinkButton, P, Errors,
  flattenErrors, TextareaInput, useStyledTheme, SecondaryButton, Button,
} from '@equifinance/shared';
import { formatUserName, formatDateTime, convertToNumber } from '@equifinance/shared/utils';
import styled from 'styled-components';
import { useState, useRef, useEffect } from 'react';
import { ChecklistSection, NotesFormConfigurationAdapter } from '../ChecklistPage';
import { PaddedGenericSection } from '../Sections/GenericSection';
import { useChecklistContext, Note } from '../../../contexts';

const NotesContainer = styled.div`${({ theme }) => `
  border-bottom: ${theme.borderWidth[1]} solid ${theme.colours.primary[3]};
  margin: 0 -${theme.padding[6]} -${theme.padding[3]};
  padding 0 ${theme.padding[6]} ${theme.smallButtonHeight};
`}`;

const NotesList = styled(FlexColumn)<{ isExpanded?: boolean }>`${({ theme, isExpanded }) => `
  gap: ${theme.padding[2]};
  max-height: ${theme.maxNotesSectionHeight};
  overflow-y: ${isExpanded ? 'auto' : 'hidden'};
`}`;

const InfoPanel = styled(FlexColumn)`${({ theme }) => `
  align-items: flex-start;
  width: ${theme.buttonSubmitWidth};
  padding: ${theme.padding[3]} ${theme.padding[4]};
  background: ${theme.colours.primary[10]};
  border-top-left-radius: ${theme.padding[2]};
  border-bottom-left-radius: ${theme.padding[2]};

  font-family: 'Open Sans';
  font-weight: ${theme.fontWeight.normal};
`}`;

const UserNameText = styled(P)`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
`}`;

const MomentText = styled(P)`${({ theme }) => `
  color: ${theme.colours.neutral[3]}; 
`}`;

const NoteArea = styled(P)`${({ theme }) => `
  padding: ${theme.padding[3]} ${theme.padding[4]};
  align-self: stretch;
  flex: 1 0 0;
  background: ${theme.colours.primary[11]};
  border-top-right-radius: ${theme.padding[2]};
  border-bottom-right-radius: ${theme.padding[2]};

  color: ${theme.colours.neutral[2]};
`}`;

const ViewAllButtonWrapper = styled.div`${({ theme }) => `
  margin: 0 -${theme.padding[6]} -${theme.smallButtonHeight};
  position: relative;
  box-shadow: 0 -1px 2px 0 rgba(16, 24, 40, 0.10);
`}`;

const ViewAllButton = styled(LinkButton)`${({ theme }) => `
  height: ${theme.smallButtonHeight};
  width: 100%;
  padding: 0;

  color: ${theme.colours.primary[1]};
  font-size: ${theme.fontSize[2]};
  font-weight: ${theme.fontWeight.bold};
  border-radius: 0;

  &:hover {
    background: ${theme.colours.primary[8]};
  };

  &:active {
    background: ${theme.colours.primary[7]};
  };

  &:focus {
      border: none;
      box-shadow: none;
      outline: none;
  };
`}`;

const EmptyDataWrapper = styled.div<{ hasNote?: boolean }>`${({ theme, hasNote }) => `
  margin-top: -${hasNote ? 0 : theme.padding[6]};
  padding-top: ${hasNote ? theme.padding[2] : 0};
`}`;

const InteractiveControls = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[4]};
  justify-content: flex-end;
`}`;

const ButtonContainer = styled.div<{ hasNote?: boolean }>`${({ theme, hasNote }) => `
  margin: -${theme.padding[4]} auto 0;
  width: fit-content;

  ${hasNote && `
    margin: 0 0 0 auto;
  `}
`}`;

type NotesProps = {
  id: string;
  title?: string;
  notesSection: ChecklistSection;
  notes?: Note[];
}

export function Notes(props: NotesProps) {
  const {
    id: checklistSectionId, title, notesSection, notes,
  } = props;
  const theme = useStyledTheme();
  const {
    postNewNote,
  } = useChecklistContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const notesListRef = useRef<HTMLDivElement>(null);
  const [notesHeight, setNotesHeight] = useState(notesListRef?.current?.clientHeight);

  const maxNotesHeight = Number(convertToNumber(theme.maxNotesSectionHeight));
  const errorMessages = flattenErrors(errors).map((err) => err?.errorMessage);

  const notesList = notes ?? [];

  const handleViewAll = () => {
    setIsExpanded(!isExpanded);

    if (isExpanded && notesListRef.current) {
      notesListRef.current.scrollTop = 0;
    }
  };

  const handleEditing = () => {
    setNote('');
    setErrors({});
    setIsEditing(!isEditing);
  };

  const handleChange = (value: string) => {
    setNote(value);

    if (errors) setErrors({});
  };

  const handlePost = async () => {
    try {
      await postNewNote({
        checklistSectionId,
        note,
      });

      setNote('');
      setErrors({});
      setIsEditing(!isEditing);
    } catch (error: any) {
      setErrors(error?.response?.data?.errors ?? {});
    }
  };

  useEffect(() => {
    // Manual check notes height to ensure
    // its height correctly at rendering
    const checkNotesHeight = () => {
      if (notesListRef?.current) {
        const rect = notesListRef?.current.getBoundingClientRect();
        setNotesHeight(rect.height);
      }
    };

    const timeoutId = setTimeout(checkNotesHeight, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [notesListRef?.current?.clientHeight, note]);

  return (
    <NotesFormConfigurationAdapter>
      <PaddedGenericSection
        id={`notes-${checklistSectionId}`}
        title={title ?? 'Notes'}
        level={2}
        iconInverted
        subSection={notesSection}
        tooltip={(notesList?.length > 0 && !title)
          ? 'Expand this section to see previous notes'
          : ''}
      >
        {notesList?.length > 0 && (
          <NotesContainer>
            <NotesList ref={notesListRef} isExpanded={isExpanded}>
              {notesList?.map((note) => (
                <Flex key={note?.id}>
                  <InfoPanel>
                    <UserNameText>{formatUserName(note?.noteFromUserName)}</UserNameText>
                    <MomentText>{formatDateTime(note?.moment, 'DD/MM/YYYY, HH:mm:ss')}</MomentText>
                  </InfoPanel>
                  <NoteArea>
                    {note?.note}
                  </NoteArea>
                </Flex>
              ))}
            </NotesList>
              {notesHeight! >= maxNotesHeight && (
              <ViewAllButtonWrapper>
                <ViewAllButton onClick={handleViewAll}>
                  {`View all (${notesList?.length})`}
                </ViewAllButton>
              </ViewAllButtonWrapper>
              )}
          </NotesContainer>
        )}
        {!title && (isEditing
          ? (
            <>
              <TextareaInput
                id={checklistSectionId}
                label="Note"
                required
                rows={3}
                value={note}
                onChange={handleChange}
                errors={errorMessages}
                touchOverride={!!errors}
              />
              <InteractiveControls>
                <SecondaryButton small onClick={handleEditing}>Cancel</SecondaryButton>
                <Button small onClick={handlePost} disabled={!note.trim()}>Post</Button>
              </InteractiveControls>
            </>
          )
          : (
            <EmptyDataWrapper hasNote={!!notesList?.length}>
              {notesList?.length === 0 && <EmptyData title="No notes found." message="" className="mi-message" />}
              <ButtonContainer hasNote={!!notesList?.length}>
                <SecondaryButton small onClick={handleEditing}>Start a note</SecondaryButton>
              </ButtonContainer>
            </EmptyDataWrapper>
          ))}
      </PaddedGenericSection>
    </NotesFormConfigurationAdapter>
  );
}
